/** Debounced search input styled for DataView toolbars. */
import { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';

interface DataViewSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Debounce delay in ms. @default 300 */
  debounceMs?: number;
  className?: string;
}

export function DataViewSearch({
  value,
  onChange,
  placeholder = 'Search...',
  debounceMs = 300,
  className,
}: DataViewSearchProps) {
  const [local, setLocal] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  function handleChange(next: string) {
    setLocal(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange(next), debounceMs);
  }

  return (
    <div
      className={`relative flex w-64 items-center rounded-full transition-shadow duration-200 focus-within:shadow-[0_0_0_2px_var(--f-accent-subtle)] ${className ?? ''}`}
      style={{ background: 'var(--f-bg-sunken)', border: '1px solid var(--f-border)' }}
    >
      <Search
        size={16}
        className="pointer-events-none ml-3.5"
        style={{ color: 'var(--f-fg-muted)' }}
      />
      <input
        type="text"
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className="w-full min-w-0 bg-transparent px-2.5 py-2 outline-none"
        style={{
          fontSize: 'var(--f-text-sm)',
          color: 'var(--f-fg)',
          fontFamily: 'var(--f-font-sans)',
        }}
      />
      {local && (
        <button
          type="button"
          onClick={() => {
            setLocal('');
            onChange('');
          }}
          className="mr-2 flex shrink-0 items-center justify-center rounded-full p-1 transition-colors hover:bg-[rgba(0,0,0,0.06)]"
          aria-label="Clear search"
        >
          <X size={14} style={{ color: 'var(--f-fg-muted)' }} />
        </button>
      )}
    </div>
  );
}
